import { useEffect, useRef, useState } from 'react';
import { useFetchStockOptions, useFetchStocks } from '../services/stock.service';
import { OptionData, StockData } from '../types/stock';
import { formatPrice } from '../helpers/common';
import { useNavigate } from 'react-router-dom';
import { Select, Slider, Pagination, DatePicker, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import Images from '../assets/images';
import { storage } from '../utils/storage';
import { IResponse, IResponseModel } from '../types/response';

const Options = () => {
    const [data, setData] = useState<OptionData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [totalItems, setTotalItems] = useState<number>(0);
    const isAuthenticated = useRef(false);
    const [stockSymbol, setStockSymbol] = useState<string>("");
    const [stocks, setStocksList] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const defaultScoreRange: [number, number] = [100, 1000];
    const [significanceScoreRange, setSignificanceScoreRange] = useState<[number, number]>(defaultScoreRange);
    const [aiScoreMarks, setAIScoreMarks] = useState<any>({});
    const [dateRange, setDateRange] = useState(["", ""]);
    const navigate = useNavigate();

    const { RangePicker } = DatePicker;

    const getStocksList = useFetchStocks({
        onSuccess: (response: IResponseModel<StockData>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((stock: { stockSymbol: string; }) => ({
                    label: stock.stockSymbol,
                    value: stock.stockSymbol,
                }));
                setStocksList(options);
            }
            else {
                setStocksList([]);
            }
        },
    });

    const getStockOptionsDetails = useFetchStockOptions({
        onSuccess: (response: IResponseModel<OptionData>) => {
            const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                ...item,
                key: index.toString(),
            }));
            setData(formattedData);
            if (response.data.length > 0 && response.data[0].recordCount !== undefined) {
                setTotalItems(response.data[0].recordCount);
            }
            setLoading(false);
        },
        onError: (error) => {
            console.error('Error fetching options:', error);
            setLoading(false);
        },
    });

    const fetchOptionsData = () => {
        setLoading(true);
        getStockOptionsDetails.mutate({
            stockSymbols: stockSymbol,
            significanceScoreRange: significanceScoreRange.join('-'),
            eventDateFrom: dateRange[0],
            eventDateTo: dateRange[1],
            pageNumber: currentPage,
            pageSize,
        });
    };

    const fetchStocksList = () => {
        setLoading(true);
        getStocksList.mutate({});
    }

    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleDateChange = (_: any, dateStrings: any) => {
        setDateRange(dateStrings);
    }

    const handleSliderChange = (value: any) => {
        setSignificanceScoreRange(value);
    };

    const handleChange = (value: any) => {
        setStockSymbol(value.join(','));
    };

    const onRefresh = () => {
        setLoading(true);
        fetchOptionsData();
    }

    useEffect(() => {
        fetchStocksList();
    }, [])

    useEffect(() => {
        const [min, max] = significanceScoreRange;
        const newMarks = {
            [min]: `${min}`,
            [max]: `${max}`,
        };
        setAIScoreMarks(newMarks);
    }, [significanceScoreRange]);

    useEffect(() => {
        isAuthenticated.current = storage.getLocalProperty("isAuthenticated") ?? false;
        if (isAuthenticated.current) {
            fetchOptionsData();
        } else {
            navigate('/');
        }
    }, [stockSymbol, dateRange, currentPage]); //significanceScoreRange

    return (
        <div className='mb-2.5'>
            <div className='p-2.5 lg:p-6 rounded-xl shadow-box'>
                <div className='block w-full md:flex justify-end lg:justify-between items-center mb-[15px]'>
                    <div>
                        <h2 className='text-[17px] hidden 2xl:block lg:text-[21px] font-bold text-black text-start'>AI Trade Recommendation</h2>
                    </div>
                    <div className='block md:items-center md:flex gap-4 w-full 2xl:w-4/5 justify-end'>
                        <div className='lg:flex gap-4 items-center w-full justify-end'>
                            <div>
                                <Space direction="vertical" size={12} className='w-full mb-2 lg:mb-0'>
                                    <RangePicker
                                        onChange={handleDateChange}
                                        className='p-2 w-full datepicker-table'
                                    />
                                </Space>
                            </div>
                            <div>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    className='w-full min-w-80 md:max-w-[600px] text-start multi-user-dropdown'
                                    placeholder="Select Stock Symbol" //"Select Companies"
                                    onChange={handleChange}
                                    options={stocks}
                                />
                            </div>
                        </div>
                        <div className='flex justify-between items-center gap-8 mt-2 md:mt-0'>
                            <div className='w-full'>
                                <p className='absolute text-sm font-semibold '>Open AI Score</p>
                                <Slider range
                                    value={significanceScoreRange}
                                    marks={aiScoreMarks}
                                    min={1} max={1000}
                                    onChange={(evt) => { handleSliderChange(evt) }}
                                    className='w-full md:w-60 top-4 range-slider h-5' />
                            </div>
                            <div>
                                <button
                                    onClick={() => {
                                        onRefresh();
                                    }}
                                    className="py-1.5 px-2 lg:px-4 text-xs lg:text-base lg:py-2 text-white border border-blue-midnight bg-blue-midnight rounded hover:bg-transparent hover:text-blue-midnight flex items-center gap-1.5"
                                >
                                    <ReloadOutlined />
                                    {loading ? "Loading..." : "Refresh"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="overflow-auto">
                    {loading ? (
                        <div className="space-y-4">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="animate-pulse flex w-full">
                                    <div className="w-1/12 h-6 bg-gray-300 rounded hidden lg:block"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-1/12 h-6 bg-gray-300 rounded mx-1"></div>
                                    <div className="w-2/12 h-6 bg-gray-300 rounded mx-1"></div>
                                </div>
                            ))}
                        </div>
                    ) : data.length === 0 ? (
                        <div>
                            <p>No records found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto h-[calc(100vh-300px)] md:h-[calc(100vh-245px)] lg:h-[calc(100vh-240px)]">
                            <table className="min-w-full w-full">
                                <thead className='sticky top-0 z-10'>
                                    <tr className='bg-[#000639] text-white text-xs lg:text-base'>
                                        <th className="hidden lg:block py-3 md:py-4 px-3.5 text-left whitespace-nowrap">Sr No.</th>
                                        <th className="py-3 md:py-4 px-2.5 lg:px-3.5 text-left whitespace-nowrap min-w-[275px] md:w-[370px] lg:min-w-[370px] xl:min-w-auto">Contract Name</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Generated</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Open AI Score</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Trade Side</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Volume</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Premium</th>
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Price</th>
                                        {/* <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Profit</th> */}
                                        <th className='py-3 md:py-4 px-2.5 lg:px-3.5 text-center whitespace-nowrap'>Ticker</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={`border-b border-black ${item.significanceScore > 500
                                                ? 'bg-green-dark-green-500 text-white'
                                                : item.significanceScore > 400
                                                    ? 'bg-green-medium-cyan-400 text-white'
                                                    : item.significanceScore > 300
                                                        ? 'bg-green-light-green-300'
                                                        : item.significanceScore > 200
                                                            ? 'bg-green-light-green-200'
                                                            : ''
                                                }`}>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 text-left hidden lg:block'>{item.rowNumber}</td>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-start'>
                                                <div className='flex items-center gap-4'>
                                                    <div
                                                        className={`${item.significanceScore > 400 ? 'text-white' : 'text-Black-500'
                                                            } `}
                                                    >
                                                        {item.contractName}
                                                    </div>
                                                    <div>
                                                        <div className='flex items-center gap-1'>
                                                            <a href={`https://finance.yahoo.com/quote/${item.yahooFinanceStockSymbol}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                <img src={Images.yahooicon} className='w-5' alt="Yahoo" />
                                                            </a>
                                                            <a href={`https://tradingview.com/chart?symbol=${item.tradingViewStockSymbol}&interval=1D`}
                                                                target="_blank"
                                                                rel="noopener noreferrer">
                                                                <img src={Images.tradingicon} className='w-5' alt="Trading" />
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap relative group text-center">
                                                <span>{item.createdAtAgo}</span>
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-3/4 lg:transform-translate-x-2/3 mb-2 hidden group-hover:block bg-blue-midnight text-white text-xs rounded px-2 py-1 z-10">
                                                    {new Date(item.createdAt).toLocaleString('en-GB', {
                                                        weekday: 'short',
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit',
                                                        hour12: true,
                                                    })}
                                                </div>
                                            </td>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>
                                                <span
                                                    className={`px-2 py-1 rounded ${item.significanceScore > 500
                                                        ? 'bg-green-deep-fir text-white border border-black'
                                                        : item.significanceScore > 300
                                                            ? 'bg-green-dark-pastel text-white border border-black'
                                                            : item.significanceScore > 200
                                                                ? 'bg-green-screamin text-black border border-black'
                                                                : item.significanceScore < 100
                                                                    ? 'bg-white text-black'
                                                                    : 'bg-green-granny text-black'
                                                        }`}
                                                >
                                                    {Math.round(item.significanceScore)}
                                                </span>
                                            </td>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>
                                                <span className={`px-1.5 lg:px-2 py-0.5 lg:py-1 rounded ${item.contractType === 'Call' ? 'bg-green-mountain-meadow bg-opacity-60 border-green-mountain-meadow border  text-black' : 'bg-red-persian bg-opacity-60 text-white border-red-persian border'}`}>
                                                    Buy {item.contractType}
                                                </span>
                                            </td>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>{formatPrice(item.contractVolume)}</td>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>${formatPrice(item.optionPremium)}</td>
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>${formatPrice(item.lastPrice)}</td>
                                            {/* <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>
                                                <span
                                                    className={`${item.significanceScore > 400
                                                        ? 'text-white'
                                                        : item.unrealizedPnL == null
                                                            ? 'text-black'
                                                            : item.unrealizedPnL < 0
                                                                ? 'text-red-700'
                                                                : 'text-green-700'
                                                        }`}
                                                >
                                                    ${item.unrealizedPnL == null ? '0.00' : formatNumberWithCommas(item.unrealizedPnL)}
                                                </span>
                                            </td> */}
                                            <td className='p-2.5 text-xs lg:text-base lg:p-3.5 whitespace-nowrap text-center'>
                                                <a
                                                    href={`https://tradingview.com/chart?symbol=${item.parentStockSymbol}&interval=1D`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-500 hover:underline"
                                                >
                                                    {`${item.parentStockSymbol} (${item.spotLastPrice})`}
                                                </a></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <Pagination
                total={totalItems}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                className='mt-2.5 md:px-6 pagination-table'
                showSizeChanger
                align='end'
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            />
        </div>
    );
};

export default Options;
